import { FC, SyntheticEvent, useRef, useState } from "react";

import ExpandMore from "@mui/icons-material/ExpandMore";
import Autocomplete from "@mui/material/Autocomplete";
import IconButton from "@mui/material/IconButton";
import ListItem from "@mui/material/ListItem";
import Popper from "@mui/material/Popper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { ClearIcon } from "@mui/x-date-pickers";

import { AutocompleteOptionType } from "../../../models/autocomplete";
import { useAuthenticatedUser } from "../../../hooks/useAuthenticatedUser";
import { RoleEnum } from "../../../models/enums";

const MODEL_OPTIONS: AutocompleteOptionType[] = [
  { title: "All events", value: "" },
  { title: "Smoke", value: "SMOKE" },
  { title: "Fire", value: "FIRE" },
  { title: "Liquid leak", value: "LIQUIDLEAK" },
  { title: "Gas leak", value: "GASLEAK" },
];

interface EventSelectProps {
  value: AutocompleteOptionType | null;
  onChange: (value: AutocompleteOptionType | null) => void;
  disabled?: boolean;
  size?: "small" | "medium";
}

const EventSelect: FC<EventSelectProps> = ({
  value,
  onChange,
  disabled = false,
  size = "small",
}) => {
  const { role } = useAuthenticatedUser();
  const hoveredRef = useRef<AutocompleteOptionType | null>(null);

  const [open, setOpen] = useState(false);

  // Hardcode gas leak for VERIFIER role
  const isVerifier = role === RoleEnum.VERIFIER;

  // If user is VERIFIER, force gas leak selection
  const verifierOption: AutocompleteOptionType = {
    title: "Gas leak",
    value: "GASLEAK",
  };

  const selectedOption: AutocompleteOptionType | undefined = isVerifier
    ? verifierOption
    : value && value.value
    ? MODEL_OPTIONS.find(option => option?.value === value.value)
    : MODEL_OPTIONS[0];

  const handleChange = (
    _: SyntheticEvent,
    newOption: AutocompleteOptionType | null
  ) => {
    if (!disabled && !isVerifier) {
      onChange(newOption && newOption.value ? newOption : null);
    }
  };

  const handleClear = () => {
    if (!disabled && !isVerifier) {
      onChange(null);
    }
  };

  const handleOnOpen = () => {
    setOpen(true);
  };

  const handleOnClose = () => {
    setOpen(false);

    hoveredRef.current = null;
  };

  const handleOnMouseEnter = (option: AutocompleteOptionType) => {
    hoveredRef.current = option;
  };

  const handleOnMouseLeave = () => {
    hoveredRef.current = null;
  };

  return (
    <Autocomplete
      fullWidth
      size={size}
      options={isVerifier ? [verifierOption] : MODEL_OPTIONS}
      getOptionLabel={(option: AutocompleteOptionType) => option?.title ?? ""}
      isOptionEqualToValue={(option, val) => option?.value === val?.value}
      value={selectedOption ?? undefined}
      onChange={handleChange}
      disabled={disabled || isVerifier}
      popupIcon={<ExpandMore />}
      open={open}
      onOpen={handleOnOpen}
      onClose={handleOnClose}
      disableClearable
      renderOption={(props, option) => (
        <ListItem
          {...props}
          onMouseEnter={() => handleOnMouseEnter(option)}
          onMouseLeave={handleOnMouseLeave}
          sx={{
            bgcolor:
              hoveredRef.current?.title === option?.title
                ? "inherit"
                : "transparent",
            "&.Mui-focused": { bgcolor: "transparent" },
            "&[aria-selected='true']": { bgcolor: "transparent" },
          }}
        >
          <Typography>{option?.title}</Typography>
        </ListItem>
      )}
      renderInput={params => (
        <TextField
          {...params}
          placeholder="Select events"
          variant="outlined"
          InputLabelProps={{
            shrink: selectedOption?.value !== "",
          }}
          InputProps={{
            ...params.InputProps,
            sx: {
              "& input": {
                color:
                  selectedOption?.value === ""
                    ? "otherTextTertiary.main"
                    : "inherit",
              },
            },
            endAdornment: (
              <>
                {value && value.value !== null && !disabled && (
                  <IconButton onClick={handleClear} size={size} sx={{ p: 0 }}>
                    <ClearIcon fontSize={size} />
                  </IconButton>
                )}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      PopperComponent={props => (
        <Popper
          {...props}
          placement="bottom-start"
          modifiers={[{ name: "offset", options: { offset: [0, 8] } }]}
        />
      )}
    />
  );
};

export default EventSelect;
